import { FileText, Link2, Mail, MessageCircle, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { safeImage } from "@/lib/api";

const KIND_ICON = { enlace: Link2, correo: Mail, archivo: FileText, texto: FileText, whatsapp: MessageCircle };
const FILTERS = [["pending", "Pendientes"], ["processed", "Procesados"],
                 ["archived", "Archivados"], ["all", "Todos"]];
const STATUS_LABEL = { pending: "Pendiente", archived: "Archivada" };

const IngestItem = ({ item, index, picked, setPicked, openProcess, onDelete }) => {
  const Icon = KIND_ICON[item.kind] || FileText;
  return (
    <div
      className="border border-border bg-card p-5 flex flex-col md:flex-row gap-4 rise"
      style={{ animationDelay: `${index * 30}ms` }}
      data-testid={`ingest-item-${item.id}`}
    >
      <input type="checkbox" className="mt-1 shrink-0 self-start"
        checked={picked.includes(item.id)}
        onChange={(e) => setPicked(e.target.checked
          ? [...picked, item.id] : picked.filter((x) => x !== item.id))}
        data-testid={`pick-item-${item.id}`} />
      {item.images?.[0] && (
        <img src={safeImage(item.images[0])} alt=""
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="w-full md:w-32 h-24 object-cover rounded-sm bg-muted" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="h-3.5 w-3.5 text-primary" />
          <span className="kicker">{item.kind}</span>
          <Badge variant={item.status === "pending" ? "secondary" : "outline"}>
            {STATUS_LABEL[item.status] || "Procesada"}
          </Badge>
          {item.images?.length > 0 && (
            <span className="text-xs text-muted-foreground">{item.images.length} imágenes</span>
          )}
        </div>
        <h3 className="text-lg font-semibold leading-snug truncate">{item.title || "Sin título"}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{item.text?.slice(0, 220)}…</p>
        <div className="text-xs text-muted-foreground mt-2 mono">
          {item.sender || item.source_url || item.created_by} · {item.created_at?.slice(0, 16).replace("T", " ")}
        </div>
      </div>
      <div className="flex md:flex-col gap-2">
        <Button size="sm" onClick={() => openProcess(item)} data-testid={`process-btn-${item.id}`}>
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Redactar
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDelete(item)}
                data-testid={`delete-item-${item.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export const IngestList = ({
  items, total, pageSize, page, setPage, filter, setFilter, query, setQuery,
  picked, setPicked, bulk, archiveOld, openProcess, onDelete,
}) => (
  <>
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <div className="kicker">Bandeja de entrada · {total} elementos</div>
      <div className="flex gap-1">
        {FILTERS.map(([v, l]) => (
          <button key={v} type="button"
            onClick={() => { setFilter(v); setPage(0); }}
            className={`text-xs px-2.5 py-1 border transition-colors ${filter === v
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border text-muted-foreground hover:border-primary/60"}`}
            data-testid={`filter-${v}`}>{l}</button>
        ))}
      </div>
      <Input value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }}
        placeholder="Buscar por titular o remitente" className="max-w-xs h-9"
        data-testid="items-search-input" />
    </div>

    {items?.length > 0 && (
      <div className="flex flex-wrap items-center gap-3 border border-border bg-muted/30 p-3 mb-3"
           data-testid="bulk-bar">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" data-testid="select-all-items"
            checked={picked.length > 0 && picked.length === items.length}
            onChange={(e) => setPicked(e.target.checked ? items.map((i) => i.id) : [])} />
          Seleccionar la página
        </label>
        <span className="text-xs mono text-muted-foreground">{picked.length} seleccionados</span>
        <Button size="sm" variant="outline" disabled={!picked.length}
          onClick={() => bulk({ action: filter === "archived" ? "restore" : "archive", ids: picked })}
          data-testid="bulk-archive-btn">
          {filter === "archived" ? "Restaurar" : "Archivar"} selección
        </Button>
        <Button size="sm" variant="outline" disabled={!picked.length}
          onClick={() => bulk({ action: "delete", ids: picked }, true)}
          data-testid="bulk-delete-btn">
          Eliminar selección
        </Button>
        <Button size="sm" variant="outline" onClick={archiveOld} data-testid="bulk-archive-old-btn">
          Archivar pendientes antiguos
        </Button>
      </div>
    )}

    <div className="space-y-3" data-testid="ingest-list">
      {(items || []).map((item, i) => (
        <IngestItem key={item.id} item={item} index={i} picked={picked} setPicked={setPicked}
                    openProcess={openProcess} onDelete={onDelete} />
      ))}
      {items && total > pageSize && (
        <div className="flex items-center justify-between gap-3 pt-2" data-testid="items-pager">
          <span className="text-xs mono text-muted-foreground">
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} de {total}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0}
              onClick={() => setPage(page - 1)} data-testid="items-prev-page">Anterior</Button>
            <Button size="sm" variant="outline" disabled={(page + 1) * pageSize >= total}
              onClick={() => setPage(page + 1)} data-testid="items-next-page">Siguiente</Button>
          </div>
        </div>
      )}
      {items?.length === 0 && (
        <p className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center">
          La bandeja está vacía. Añada un enlace, cargue archivos o revise el correo.
        </p>
      )}
    </div>
  </>
);
